import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import Grid from '~/components/core/Atomic/Grid/Grid';

import CheckboxFormik from '../CheckboxFormik';
import { getAllSearchableContactRoles } from '../communications/ContactUtils';
import ContactTextFieldFormik from '../ContactTextFieldFormik';
import LoadingIndicator from '../LoadingIndicator';
import useOrganization from '../OrganizationContext';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  MultiSelectTextFieldFormik,
} from '../TextFieldFormik';

// Render the comma-joined human label for an mselect template field's selected values.
// Guards against non-array `selected` (e.g. when MUI invokes renderValue before Formik has
// initialized the field as []). See SENTRY-CMS-REACT-FE-1PK.
export function renderMselectValue(selected, optionsLabelByValue) {
  if (!Array.isArray(selected) || !optionsLabelByValue) {
    return '';
  }
  return selected
    .map((option) => optionsLabelByValue[option])
    .filter(Boolean)
    .join(', ');
}

export function getDocumentTemplateDynamicFieldsValidation(templateDynamicFields) {
  if (!templateDynamicFields) {
    return {};
  }

  let fieldValidations = {};
  templateDynamicFields.forEach((templateField) => {
    switch (templateField.type) {
      case 'mselect':
        fieldValidations[templateField.id] = Yup.array();
        return;
      case 'string':
        fieldValidations[templateField.id] = Yup.string().required('Required');
        return;
      case 'mstring':
        fieldValidations[templateField.id] = Yup.string().required('Required');
        return;
      case 'ostring':
      case 'omstring':
        fieldValidations[templateField.id] = Yup.string();
        return;
      case 'checkbox':
        fieldValidations[templateField.id] = Yup.boolean();
        return;
      case 'contact':
      case 'ocontact': {
        fieldValidations[`${templateField.id}_id`] = Yup.number();
        if (templateField.type === 'contact') {
          fieldValidations[`${templateField.id}_id`] = fieldValidations[`${templateField.id}_id`].required('Required');
        }
        const hasAddressValidation =
          templateField.sub.includes('address_1') ||
          templateField.sub.includes('address_2') ||
          templateField.sub.includes('country');
        const hasEmailValidation = templateField.sub.includes('email_address');

        if (hasAddressValidation || hasEmailValidation) {
          let contactValidation = Yup.object();

          if (hasAddressValidation) {
            contactValidation = contactValidation.test(
              'is-valid',
              ({ originalValue }) =>
                `Contact must contain address1, city and ${originalValue.country === 'US' ? 'state' : 'country'}`,
              function test(contact) {
                const contactId = this.parent[`${templateField.id}_id`];
                return (
                  !contactId ||
                  (contact &&
                    contact.street_address1 &&
                    contact.city &&
                    (contact.country === 'US' ? contact.state : contact.country))
                );
              }
            );
          }

          if (hasEmailValidation) {
            contactValidation = contactValidation
              .test('is-email-exists', 'Contact must contain at least one email address', function test(contact) {
                const contactId = this.parent[`${templateField.id}_id`];
                return !contactId || (contact && contact.emails && contact.emails.length > 0);
              })
              .test('is-email-not-empty', 'Invalid email', function test(contact) {
                const contactId = this.parent[`${templateField.id}_id`];
                return (
                  !contactId ||
                  (contact &&
                    contact.emails &&
                    contact.emails[0] &&
                    typeof contact.emails[0].email === 'string' &&
                    contact.emails[0].email.trim() !== '')
                );
              });
          }

          fieldValidations[templateField.id] = contactValidation;
        }
        return;
      }
      case 'date':
        fieldValidations[templateField.id] = Yup.date().required('Required');
        return;
      case 'odate':
        fieldValidations[templateField.id] = Yup.date();
        return;
      case 'monetary':
      case 'number':
        fieldValidations[templateField.id] = Yup.number().required('Required');
        return;
      case 'omonetary':
      case 'onumber':
        fieldValidations[templateField.id] = Yup.number();
        return;
      case 'esign_contact':
        // Handled separately by UploadDocumentFromTemplate
        return;
      default:
        throw Error(`Unknown document template field type: ${templateField.type}, field id: ${templateField.id}`);
    }
  });

  return fieldValidations;
}

export function extractDynamicFieldsValuesFromFormikValues(templateDynamicFields, values) {
  // This function comes in handy to prevent passing the contact object and more to GET functions

  if (!templateDynamicFields) {
    return {};
  }

  let valuesExtracted = {};
  templateDynamicFields.forEach((templateField) => {
    switch (templateField.type) {
      case 'contact':
      case 'ocontact':
        valuesExtracted[`${templateField.id}_id`] = values[`${templateField.id}_id`];
        break;
      case 'string':
      case 'ostring':
      case 'date':
      case 'odate':
      case 'monetary':
      case 'number':
      case 'omonetary':
      case 'onumber':
      case 'mselect':
      case 'checkbox':
      case 'mstring':
      case 'omstring':
        valuesExtracted[templateField.id] = values[templateField.id];
        break;
      case 'esign_contact':
        // Handled separately by UploadDocumentFromTemplate
        break;
      default:
        throw Error(`Unknown document template field type: ${templateField.type}, field id: ${templateField.id}`);
    }
  });

  return valuesExtracted;
}

export function TemplateDynamicFieldsFragment({ templateDynamicFields, isLoading, isError }) {
  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  if (!templateDynamicFields) {
    return null;
  }

  // Filter out esign_contact fields (handled separately in UploadDocumentFromTemplate)
  const renderableFields = templateDynamicFields.filter((field) => field.type !== 'esign_contact');

  return (
    <>
      {renderableFields.map((templateField) => (
        <Grid item xs={12} key={templateField.id}>
          <TemplateDynamicField templateField={templateField} />
        </Grid>
      ))}
    </>
  );
}

TemplateDynamicFieldsFragment.propTypes = {
  templateDynamicFields: PropTypes.array,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

function TemplateDynamicField({ templateField }) {
  const { classes } = useStyles();
  const { organizationContactRolesDict } = useOrganization();

  const fieldIds = {
    id: templateField.id,
    label: templateField.label,
    className: classes.textField,
    fullWidth: true,
  };

  switch (templateField.type) {
    case 'string':
    case 'ostring':
      return <TextFieldFormik {...fieldIds} />;
    case 'mstring':
    case 'omstring':
      return <TextFieldFormik {...fieldIds} multiline rows={4} />;
    case 'contact':
    case 'ocontact':
      return (
        <ContactTextFieldFormik
          acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
          {...fieldIds}
          fixedSearchResults
        />
      );
    case 'date':
    case 'odate':
      return <DatePickerTextFieldFormik {...fieldIds} />;
    case 'monetary':
    case 'omonetary':
      return <MonetaryValueTextFieldFormik {...fieldIds} />;
    case 'number':
    case 'onumber':
      return <TextFieldFormik {...fieldIds} type="number" />;
    case 'mselect':
      return (
        <MultiSelectTextFieldFormik
          {...fieldIds}
          options={Object.keys(templateField.options)}
          renderValue={(selected) => renderMselectValue(selected, templateField.options)}
          renderOption={(option) => templateField.options[option]}
        />
      );
    case 'checkbox':
      return <CheckboxFormik {...fieldIds} />;
    case 'esign_contact':
      // Filtered out - uses existing signature UI in UploadDocumentFromTemplate
      return null;
    default:
      throw Error(`Unknown document template field type: ${templateField.type}, field id: ${templateField.id}`);
  }
}

TemplateDynamicField.propTypes = {
  templateField: PropTypes.object.isRequired,
};
